import Axios from "axios";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTheme } from "styled-components/macro";
import styled from "styled-components/macro";
import type { Getter } from "@tanstack/react-table";
import ReactTooltip from "react-tooltip";
import { useAuthContext } from "../../../../../components/Auth";
import {
  GhostButtonMediumV2,
  TertiaryButtonMediumV2,
} from "../../../../../components/Buttons/Buttons";
import {
  DeleteIcon,
  EditIcon,
  NotApplicableIcon,
  PlusIcon,
  QuoteIcon,
} from "../../../../../components/Icons/Icons";
import { Notifications } from "../../../../../components/Notifications/NotificationsContext";
import { TablePageLayout } from "../../../../../components/TablePage/TablePageLayout";
import { FilterBy } from "../../../../../components/FilterBy/FilterBy";
import { NumericFilterButton } from "../../../../../components/Filters/NumericFilterInput";
import type { ChipType } from "../../../../../components/Chips/Chips";
import { useRoutePath } from "../../../../../util/Routing";
import { useStoreState } from "../../../../../util/util";
import { TablePlaceholder, toTitleCase } from "../../../../../util/util";
import {
  getAxiosErrorStatusAndMessage,
  isDuplicateCollectionRowConflict,
} from "../../../../../util/collectionConflictUtils";
import { DuplicateItemModal } from "../../../../SharedPages/ProductDetailPage/Approvals/ConfirmationModals";
import {
  CollectionAttributeDisplayValue,
  DocumentCollectionValue,
  getCollectionAttributeDisplayValue,
} from "../../../../../util/collectionAttributeDisplayValue";
import { DocumentMoreModal } from "../../../../../components/AttributeGroupFormGenerator/DocumentAttribute/DocumentMoreModal";
import { ObjectReferenceMoreModal } from "../../../../../components/ObjectReference/ObjectReferenceMoreModal";
import { ProductCardsMoreModal } from "../../../../../components/ProductCardsGrid/ProductCardsMoreModal";
import type {
  ObjectReferenceResult,
  ProductReference,
} from "../../../../../types/types.PIM";
import { getAttributeValue } from "../../PIM/components/ProductCollectionSchemaTable/ProductCollectionSchema.util";
import { AccordionCard } from "../../../../../components/AccordionCard/AccordionCard";
import type {
  IRow,
  FormValue,
} from "../../PIM/components/ProductCollectionSchemaTable/type";
import type {
  AttributeSchema,
  CollectionSchema,
  AttributeValue,
} from "../../../../../types/types.PIM";
import type { DataMutate, OptionType, UUID } from "../../../../../types/types";
import { NUMERIC_FILTER_OPERATORS } from "../../../../../types/types";
import type { EntityProfile, EntityType } from "../../../../../types/types.MDM";
import { ENTITY_MODIFY_PERMISSIONS } from "../../../../../types/types.MDM";
import { useHistory } from "react-router-dom";
import { endpoints } from "../../../../../endpoints";
import {
  toUrlKey,
  valueToDisplayStrings,
  matchesFilter,
  UNASSIGNED_FILTER_ID,
  getHighlightSegments,
  SearchHighlight,
  areSearchParamsEqual,
} from "../../../../../util/collectionSearchUtils";
import { useListFilterCarryOver } from "../../../../../util/useListFilterCarryOver";
import { EntityCollectionSlideOut } from "./EntityCollectionSlideOut";
import { ConfirmDialog } from "../../../../../components/ConfirmDialog/ConfirmDialog";
import { ObjectReferenceChangeConfirmDialog } from "../../../../../components/ObjectReference/ObjectReferenceChangeConfirmDialog";
import {
  shouldConfirmObjectReferenceChange,
  shouldShowObjectReferenceAttribute,
} from "../../../../../components/ObjectReference/utils";
import { getAssetAssociateEndpoint } from "../../../../../util/assetEndpoints";
import { NonVisibileContainer } from "../../PIM/components/ProductCollectionSchemaTable/ProductCollectionSchemaTable";
import axios from "axios";
import { isDocumentAttribute } from "../../../../../components/AttributeGroupFormGenerator/DocumentAttribute/documentAsset.utils";
import {
  collectionAttributeValueHasDisplayValue,
  collectionHasDisplayableValues,
} from "./utils";
import { PROFILE_DETAIL_CLASSES } from "../../../../../layout/shared/ProfileDetailLayout";
import { ChangeRequestFormModal } from "../../../../../components/ChangeRequestForm/ChangeRequestModal";
import {
  ChangeRequestFormValues,
  submitChangeRequest,
} from "../../../../../components/ChangeRequestForm/changerequest.util";

const CollectionTableContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.resolve("spacing_3xl")};
  overflow: auto;
  & div[class*="TableWrapper"]:not([class*="ScrollTable"]) {
    width: 100%;
    overflow: visible;
    td:last-child:has(> #table-action-buttons) {
      position: sticky;
      right: 0;
    }
    table:has(td:last-child > #table-action-buttons) th:last-child {
      position: sticky;
      right: 0;
    }
  }
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

const HeaderWithIcon = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
`;

interface EntityCollectionCardProps {
  collection: CollectionSchema;
  entity: EntityProfile;
  entityType: EntityType;
  tenantId: string;
  mutateEntity: DataMutate<EntityProfile>;
  isEditable: boolean;
}

function getCollectionEndpoint(
  entityType: EntityType,
  tenantId: string,
  entityId: string,
  collectionId: string
): string {
  switch (entityType) {
    case "customer":
      return endpoints.v2_tenants_id_customers_id_collections_id(
        tenantId,
        entityId,
        collectionId
      );
    case "supplier":
      return endpoints.v2_tenants_id_suppliers_id_collections_id(
        tenantId,
        entityId,
        collectionId
      );
    case "distributor":
      return endpoints.v2_tenants_id_distributors_id_collections_id(
        tenantId,
        entityId,
        collectionId
      );
  }
}

function getCollectionRowEndpoint(
  entityType: EntityType,
  tenantId: string,
  entityId: string,
  collectionId: string,
  rowId: string
): string {
  switch (entityType) {
    case "customer":
      return endpoints.v2_tenants_id_customers_id_collections_id_rows_id(
        tenantId,
        entityId,
        collectionId,
        rowId
      );
    case "supplier":
      return endpoints.v2_tenants_id_suppliers_id_collections_id_rows_id(
        tenantId,
        entityId,
        collectionId,
        rowId
      );
    case "distributor":
      return endpoints.v2_tenants_id_distributors_id_collections_id_rows_id(
        tenantId,
        entityId,
        collectionId,
        rowId
      );
  }
}

function isEmpty(value: FormValue[string] | AttributeValue["value"]): boolean {
  if (value === undefined || value === null || value === "" || value === " ") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === "object") {
    if (!value) return true;
    if ("value" in value) {
      return isEmpty((value as OptionType<string>).value);
    }
    if ("url" in value) {
      return isEmpty((value as { url: string }).url);
    }
    // For address objects (or any plain object), check if all values are empty
    return Object.values(value).every((v) => isEmpty(v as any));
  }
  return false;
}

export function EntityCollectionCard({
  collection,
  entity,
  entityType,
  tenantId,
  mutateEntity,
  isEditable,
}: EntityCollectionCardProps): JSX.Element | null {
  const { entity_id } = useParams<{ entity_id: string }>();
  const { t } = useTranslation();
  const theme = useTheme();
  const { user, roleIsSellerAdmin, hasPermission } = useAuthContext();
  const {
    tenant_id,
    storefront_metadata: { object_reference_enabled, enable_change_requests },
  } = useStoreState();
  const { storePath, adminPath } = useRoutePath();
  const { notifySuccess, notifyError } = useContext(Notifications);
  const history = useHistory();
  const { search } = useLocation();
  // Stable URL namespace derived from collection name so multiple tables on
  // the same page each own their own search / filter params.
  const urlPrefix = useMemo(() => toUrlKey(collection.name), [collection.name]);

  const [searchQuery, setSearchQuery] = useState<string>(() => {
    const params = new URLSearchParams(search);
    return params.get(`${urlPrefix}_q`) ?? "";
  });

  const [activeFilters, setActiveFilters] = useState<
    Record<string, ChipType[]>
  >(() => {
    const params = new URLSearchParams(search);
    const result: Record<string, ChipType[]> = {};
    const filterPrefix = `${urlPrefix}_f_`;
    // Build colNameSlug → colId lookup so we can restore keyed by col.id
    const nameSlugToId: Record<string, string> = {};
    collection.columns.forEach((col) => {
      if (!col.is_filter_eligible) return;
      nameSlugToId[toUrlKey(col.name)] = col.id;
    });
    params.forEach((value, key) => {
      if (key.startsWith(filterPrefix)) {
        const colNameSlug = key.slice(filterPrefix.length);
        const colId = nameSlugToId[colNameSlug];
        if (!colId) return;
        const chips = value
          .split(",")
          .filter(Boolean)
          .map((v) => {
            const decoded = decodeURIComponent(v);
            // Restore the Unassigned sentinel with its special ID so matching works.
            if (decoded === UNASSIGNED_FILTER_ID) {
              return { name: t("Unassigned"), id: UNASSIGNED_FILTER_ID };
            }
            return { name: decoded, id: decoded };
          });
        if (chips.length > 0) result[colId] = chips;
      }
    });
    return result;
  });

  const listSearchStorageKey = `${entityType}_search`;
  const entityCarryId = entity_id ?? entity.id;

  const translateDisplayName = useCallback(
    (displayName: string) => t([displayName]),
    [t]
  );
  const { markDismissedIfCarriedOver } = useListFilterCarryOver({
    domain: entityType,
    scopeId: entityCarryId,
    collectionId: collection.id,
    listSearchStorageKey,
    columns: collection.columns,
    translateDisplayName,
    setActiveFilters,
  });

  // Keep a ref so the URL-sync effect can read the latest column definitions
  // without depending on them — SWR revalidations return a new array reference
  // which would otherwise re-run the effect mid-keystroke.
  const collectionColumnsRef = useRef(collection.columns);
  collectionColumnsRef.current = collection.columns;

  // Sync search and filter state back to the URL whenever they change.
  useEffect(() => {
    const params = new URLSearchParams(search);
    const filterPrefix = `${urlPrefix}_f_`;

    if (searchQuery) {
      params.set(`${urlPrefix}_q`, searchQuery);
    } else {
      params.delete(`${urlPrefix}_q`);
    }

    // Remove stale filter params for this collection then re-add active ones.
    const toDelete: string[] = [];
    params.forEach((_, key) => {
      if (key.startsWith(filterPrefix)) toDelete.push(key);
    });
    toDelete.forEach((k) => params.delete(k));

    Object.entries(activeFilters).forEach(([colId, chips]) => {
      if (chips.length > 0) {
        const col = collectionColumnsRef.current.find((c) => c.id === colId);
        const colKey = col ? toUrlKey(col.name) : colId;
        params.set(
          `${filterPrefix}${colKey}`,
          chips
            .map((c) =>
              c.id === UNASSIGNED_FILTER_ID
                ? UNASSIGNED_FILTER_ID
                : encodeURIComponent(c.name)
            )
            .join(",")
        );
      }
    });
    if (!areSearchParamsEqual(params, search)) {
      history.replace({ search: params.toString() });
    }
  }, [searchQuery, activeFilters, urlPrefix, history, search]);
  const [showSlideOut, setShowSlideOut] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isNewRow, setIsNewRow] = useState(false);
  const [editingRow, setEditingRow] = useState<IRow | null>(null);
  const [changeRequestFormRow, setChangeRequestFormRow] =
    useState<IRow | null>(null);
  const [editingRowUuid, setEditingRowUuid] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    show: boolean;
    rowId: string | null;
  }>({ show: false, rowId: null });
  const [pendingObjectRefRowUpdate, setPendingObjectRefRowUpdate] =
    useState<{
      rowId: string;
      values: {
        attribute_id: string;
        value:
          | string
          | { display_text: string; url: string }
          | { asset_id: string };
      }[];
      valuesToDelete: string[];
      documentAssetIds: string[];
    } | null>(null);

  const columns = collection.columns;
  const visibleColumns = useMemo(
    () =>
      columns
        .filter(({ is_visible }) => is_visible)
        ?.filter((col) =>
          shouldShowObjectReferenceAttribute({
            inputType: col.input_type,
            objectReferenceEnabled: object_reference_enabled,
          })
        ),
    [columns, object_reference_enabled]
  );

  const hasEditableColumns = useMemo(
    () => visibleColumns.some((col) => col.is_editable !== false),
    [visibleColumns]
  );
  const [isSubmittingApplicability, setIsSubmittingApplicability] =
    useState(false);

  const [pendingAction, setPendingAction] =
    useState<{
      columnId: UUID;
      newStatus: boolean;
    } | null>(null);

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDuplicateRowError, setShowDuplicateRowError] = useState(false);
  const [documentMoreDocs, setDocumentMoreDocs] =
    useState<{
      docs: DocumentCollectionValue[];
      attributeName: string;
    } | null>(null);
  const [objectReferenceMore, setObjectReferenceMore] =
    useState<{
      references: ObjectReferenceResult[];
      attributeName: string;
      targetObjectType?: string;
    } | null>(null);
  const [productReferenceMore, setProductReferenceMore] =
    useState<{
      references: ProductReference[];
      attributeName: string;
    } | null>(null);

  const tableData = useMemo(() => {
    const visibleColumnById = new Map(
      visibleColumns.map((column) => [column.id, column])
    );

    return (collection.value_rows ?? []).reduce<IRow[]>((acc, row) => {
      const hasDisplayableValue = row.values.some((attrValue) => {
        const column = visibleColumnById.get(attrValue.attribute_id);
        if (!column) {
          return false;
        }
        return collectionAttributeValueHasDisplayValue(
          attrValue,
          column.input_type
        );
      });

      if (!hasDisplayableValue) {
        return acc;
      }

      const temp = row.values.reduce<IRow>(
        (valueAcc, attrValue) => ({
          ...valueAcc,
          [attrValue.attribute_id]: getAttributeValue(attrValue, valueAcc),
          uuid: row.row_id,
          edit: false,
        }),
        {
          uuid: row.row_id,
          edit: false,
          row_editable: true,
        }
      );
      return [...acc, temp];
    }, []);
  }, [collection.value_rows, visibleColumns]);

  const columnFilterOptions = useMemo(() => {
    // Base: apply search first (match against display strings so date format works)
    const query = searchQuery.trim().toLowerCase();
    const searchFiltered = query
      ? tableData.filter((row) =>
          visibleColumns.some((col) => {
            const value = row[col.id as keyof IRow];
            return valueToDisplayStrings(col, value).some((s) =>
              s.toLowerCase().includes(query)
            );
          })
        )
      : tableData;

    const result: Record<string, ChipType[]> = {};
    visibleColumns
      .filter((col) => col.is_filter_eligible)
      .forEach((col) => {
        // For each column, apply all OTHER active filters (cascade)
        let dataForCol = searchFiltered;
        Object.entries(activeFilters).forEach(([colId, chips]) => {
          if (colId === col.id || chips.length === 0) return;
          const siblingCol = visibleColumns.find((c) => c.id === colId);
          if (!siblingCol) return;
          dataForCol = dataForCol.filter((row) =>
            matchesFilter(siblingCol, row[colId as keyof IRow], chips)
          );
        });

        const seen = new Set<string>();
        const options: ChipType[] = [];
        let hasEmptyRows = false;

        dataForCol.forEach((row) => {
          const cellValue = row[col.id as keyof IRow];
          const displayStrings = valueToDisplayStrings(col, cellValue);
          if (displayStrings.length === 0) {
            hasEmptyRows = true;
            return;
          }
          displayStrings.forEach((str) => {
            const trimmed = str.trim();
            if (trimmed && !seen.has(trimmed)) {
              seen.add(trimmed);
              options.push({ name: trimmed, id: trimmed });
            }
          });
        });

        const filteredOptions =
          col.input_type === "date"
            ? options.filter((o) => !/^\d{4}-\d{2}-\d{2}/.test(o.name))
            : options;

        if (hasEmptyRows) {
          filteredOptions.push({
            name: t("Unassigned"),
            id: UNASSIGNED_FILTER_ID,
          });
        }

        if (filteredOptions.length > 0) {
          result[col.id] = filteredOptions;
        }
      });
    return result;
  }, [tableData, visibleColumns, searchQuery, activeFilters, t]);

  // Only prune the Unassigned sentinel when it is no longer a valid option.
  // Never drop value chips based on `columnFilterOptions` — that list only
  // reflects values present in *this* collection's current rows, but
  // list-page carry-over often references values absent from this entity's
  // rows, and wiping them clears the URL and looks like carry-over never ran
  // (see useListFilterCarryOver.ts).
  useEffect(() => {
    if (tableData.length > 0) {
      setActiveFilters((prev) => {
        let changed = false;
        const next: Record<string, ChipType[]> = {};

        Object.entries(prev).forEach(([colId, chips]) => {
          const hasUnassignedChip = chips.some(
            (chip) => chip.id === UNASSIGNED_FILTER_ID
          );
          if (!hasUnassignedChip) {
            next[colId] = chips;
            return;
          }
          const unassignedStillValid = !!columnFilterOptions[colId]?.some(
            (o) => o.id === UNASSIGNED_FILTER_ID
          );
          if (unassignedStillValid) {
            next[colId] = chips;
            return;
          }
          changed = true;
          next[colId] = chips.filter(
            (chip) => chip.id !== UNASSIGNED_FILTER_ID
          );
        });

        return changed ? next : prev;
      });
    }
  }, [columnFilterOptions, tableData]);

  const handleFilterChange = useCallback(
    ({ values, filter }: { values: ChipType[]; filter: string }) => {
      setActiveFilters((prev) => {
        const next = { ...prev, [filter]: values };
        const hasAny = Object.values(next).some((chips) => chips.length > 0);
        if (!hasAny) {
          markDismissedIfCarriedOver();
        }
        return next;
      });
    },
    [markDismissedIfCarriedOver]
  );

  const handleClearAllFilters = useCallback(() => {
    markDismissedIfCarriedOver();
    setActiveFilters({});
  }, [markDismissedIfCarriedOver]);

  const filteredTableData = useMemo(() => {
    let data = tableData;

    const query = searchQuery.trim().toLowerCase();
    if (query) {
      data = data.filter((row) =>
        visibleColumns.some((col) => {
          const value = row[col.id as keyof IRow];
          return valueToDisplayStrings(col, value).some((s) =>
            s.toLowerCase().includes(query)
          );
        })
      );
    }

    Object.entries(activeFilters).forEach(([colId, chips]) => {
      if (chips.length === 0) return;
      const col = visibleColumns.find((c) => c.id === colId);
      if (!col) return;
      data = data.filter((row) =>
        matchesFilter(col, row[colId as keyof IRow], chips)
      );
    });

    return data;
  }, [tableData, searchQuery, visibleColumns, activeFilters]);

  const handleAddRow = () => {
    setEditingRow(null);
    setEditingRowUuid(null);
    setIsNewRow(true);
    setShowSlideOut(true);
  };

  const handleEditRow = (row: IRow) => {
    setEditingRow(row);
    setEditingRowUuid(row.uuid);
    setIsNewRow(false);
    setShowSlideOut(true);
  };

  const handleChangeRequestForm = (row: IRow) => {
    setChangeRequestFormRow(row);
    setShowForm(true);
  };

  const handleCloseSlideOut = () => {
    setShowSlideOut(false);
    setEditingRow(null);
    setEditingRowUuid(null);
    setIsNewRow(false);
  };

  const handleSubmitRow = async (
    formValues: FormValue & { row_id: string }
  ) => {
    const { row_id, ...values } = formValues;
    const documentAssetIds: string[] = [];
    setIsSubmitting(true);
    try {
      if (isNewRow) {
        // Add row
        const apiValues: {
          attribute_id: string;
          value:
            | string
            | { display_text: string; url: string }
            | { asset_id: string };
        }[] = [];
        Object.entries(values).forEach(([attributeId, value]) => {
          if (isEmpty(value)) return;
          const column = columns.find((col) => col.id === attributeId);
          if (isDocumentAttribute(column?.input_type)) {
            const items = Array.isArray(value)
              ? (value as Array<{ asset_id: string }>)
              : [value];
            items.forEach((v) => {
              const assetId = (v as { asset_id?: string }).asset_id;
              if (assetId) {
                documentAssetIds.push(assetId);
                apiValues.push({
                  attribute_id: attributeId,
                  value: { asset_id: assetId },
                });
              }
            });
            return;
          }
          if (Array.isArray(value)) {
            (value as Array<unknown>).forEach((v) => {
              const str =
                typeof v === "object" && v && "value" in v
                  ? (v as { value: string }).value
                  : String(v);
              if (!isEmpty(str)) {
                apiValues.push({ attribute_id: attributeId, value: str });
              }
            });
          } else {
            const resolved =
              typeof value === "object" && value && "value" in value
                ? (value as OptionType<string>).value
                : value;
            if (!isEmpty(resolved)) {
              apiValues.push({
                attribute_id: attributeId,
                value: resolved as
                  | string
                  | { display_text: string; url: string },
              });
            }
          }
        });

        if (apiValues.length === 0 && documentAssetIds.length === 0) {
          notifyError(t("Please enter at least one value."));
          return;
        }

        await Axios.patch(
          getCollectionEndpoint(entityType, tenantId, entity.id, collection.id),
          { rows: [{ values: apiValues }] }
        );
        if (documentAssetIds.length > 0) {
          await Axios.patch(
            getAssetAssociateEndpoint(entityType, tenantId, entity.id),
            { asset_ids: documentAssetIds }
          );
        }
        await mutateEntity();
        notifySuccess(t("Row added successfully."));
      } else {
        // Edit row
        const targetRow = collection.value_rows?.find(
          (r) => r.row_id === row_id
        );
        const apiValues: {
          attribute_id: string;
          value:
            | string
            | { display_text: string; url: string }
            | { asset_id: string };
        }[] = [];
        const valuesToDelete: string[] = [];
        let requiresObjectRefConfirmation = false;

        if (targetRow) {
          const attrToValueMap = targetRow.values.reduce<
            Record<string, AttributeValue | AttributeValue[]>
          >((acc, av) => {
            const existing = acc[av.attribute_id];
            acc[av.attribute_id] = existing
              ? [...(Array.isArray(existing) ? existing : [existing]), av]
              : av;
            return acc;
          }, {});

          Object.entries(values).forEach(([attributeId, value]) => {
            const column = columns.find((col) => col.id === attributeId);
            const existingValue = attrToValueMap[attributeId] as
              | AttributeValue
              | AttributeValue[]
              | undefined;

            if (column?.input_type === "object_reference") {
              const hasChanged = shouldConfirmObjectReferenceChange({
                existingValue,
                nextFormValue: value,
              });
              if (!hasChanged) {
                return;
              }
              requiresObjectRefConfirmation = true;
            }

            if (isEmpty(value)) {
              // Mark existing for deletion if clearing
              if (existingValue) {
                if (Array.isArray(existingValue)) {
                  valuesToDelete.push(...existingValue.map((v) => v.id));
                } else if (!isEmpty(existingValue.value)) {
                  valuesToDelete.push(existingValue.id);
                }
              }
              return;
            }

            // Marks existing values for deletion.Will be replaced by new values below.
            // This does not work for document attributes, because they are associated with assets.
            if (existingValue && !isDocumentAttribute(column?.input_type)) {
              if (Array.isArray(existingValue)) {
                valuesToDelete.push(...existingValue.map((v) => v.id));
              } else {
                valuesToDelete.push(existingValue.id);
              }
            }

            if (Array.isArray(value)) {
              (value as Array<unknown>).forEach((v) => {
                if (typeof v === "object" && v && "asset_id" in v) {
                  const assetId = (v as { asset_id: string }).asset_id;
                  apiValues.push({
                    attribute_id: attributeId,
                    value: { asset_id: assetId },
                  });
                  documentAssetIds.push(assetId);
                } else {
                  const str =
                    typeof v === "object" && v && "value" in v
                      ? (v as { value: string }).value
                      : String(v);
                  if (!isEmpty(str)) {
                    apiValues.push({ attribute_id: attributeId, value: str });
                  }
                }
              });
            } else {
              const resolved =
                typeof value === "object" && value && "value" in value
                  ? (value as OptionType<string>).value
                  : value;
              if (!isEmpty(resolved)) {
                apiValues.push({
                  attribute_id: attributeId,
                  value: resolved as
                    | string
                    | { display_text: string; url: string },
                });
              }
            }
          });
        }

        if (
          apiValues.length === 0 &&
          valuesToDelete.length === 0 &&
          documentAssetIds.length === 0
        ) {
          notifyError(t("No changes to save."));
          return;
        }

        if (requiresObjectRefConfirmation) {
          setPendingObjectRefRowUpdate({
            rowId: row_id,
            values: apiValues,
            valuesToDelete,
            documentAssetIds,
          });
          return;
        }

        await Axios.patch(
          getCollectionRowEndpoint(
            entityType,
            tenantId,
            entity.id,
            collection.id,
            row_id
          ),
          { values: apiValues, values_to_delete: valuesToDelete }
        );
        if (documentAssetIds.length > 0) {
          await Axios.patch(
            getAssetAssociateEndpoint(entityType, tenantId, entity.id),
            { asset_ids: documentAssetIds }
          );
        }
        await mutateEntity();
        notifySuccess(t("Row updated successfully."));
      }
      handleCloseSlideOut();
    } catch (error) {
      const { message } = getAxiosErrorStatusAndMessage(error);
      if (isDuplicateCollectionRowConflict(error)) {
        setShowDuplicateRowError(true);
      } else {
        notifyError(
          message ?? t("Could not save row. Something went wrong."),
          { error }
        );
      }
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmObjectRefRowUpdate = async () => {
    if (!pendingObjectRefRowUpdate) {
      return;
    }
    setIsSubmitting(true);
    try {
      await Axios.patch(
        getCollectionRowEndpoint(
          entityType,
          tenantId,
          entity.id,
          collection.id,
          pendingObjectRefRowUpdate.rowId
        ),
        {
          values: pendingObjectRefRowUpdate.values,
          values_to_delete: pendingObjectRefRowUpdate.valuesToDelete,
        }
      );
      if (pendingObjectRefRowUpdate.documentAssetIds.length > 0) {
        await Axios.patch(
          getAssetAssociateEndpoint(entityType, tenantId, entity.id),
          { asset_ids: pendingObjectRefRowUpdate.documentAssetIds }
        );
      }
      await mutateEntity();
      notifySuccess(t("Row updated successfully."));
      handleCloseSlideOut();
    } catch (error) {
      const { message } = getAxiosErrorStatusAndMessage(error);
      if (isDuplicateCollectionRowConflict(error)) {
        setShowDuplicateRowError(true);
      } else {
        notifyError(
          message ?? t("Could not save row. Something went wrong."),
          { error }
        );
      }
      throw error;
    } finally {
      setIsSubmitting(false);
      setPendingObjectRefRowUpdate(null);
    }
  };

  const handleDeleteRow = async (rowId: string) => {
    try {
      await Axios.delete(
        getCollectionRowEndpoint(
          entityType,
          tenantId,
          entity.id,
          collection.id,
          rowId
        )
      );
      await mutateEntity();
      notifySuccess(t("Row deleted successfully."));
    } catch (error) {
      const { message } = getAxiosErrorStatusAndMessage(error);
      if (isDuplicateCollectionRowConflict(error)) {
        setShowDuplicateRowError(true);
      } else {
        notifyError(
          message ?? t("Could not delete row. Something went wrong."),
          { error }
        );
      }
    }
    setDeleteConfirm({ show: false, rowId: null });
  };

  const tableColumns = useMemo(() => {
    const cols = visibleColumns
      .filter((col: AttributeSchema) => col.input_type !== "multiline_entry")
      .map((col: AttributeSchema) => {
        const contextMenuItems = [
          ...(col.is_editable === false || isSubmittingApplicability
            ? []
            : [
                {
                  key: "toggle-applicability",
                  label: col.is_not_applicable
                    ? t("Mark as Applicable")
                    : t("Mark as Not Applicable"),
                  value: col.is_not_applicable ? "mark_applicable" : "mark_na",
                  icon: (
                    <NotApplicableIcon
                      width={16}
                      height={16}
                      fill={
                        col.is_not_applicable
                          ? theme.errorColor
                          : theme.secondaryIconColor
                      }
                    />
                  ),
                },
              ]),
        ];

        const contextMenu = {
          items: contextMenuItems,
          onSelect: (value: string) => {
            const newStatus = value === "mark_na";
            setPendingAction({
              columnId: col.id,
              newStatus,
            });
            setShowConfirmDialog(true);
          },
        };

        return {
          header: () => {
            const label = col.display_name ? t([col.display_name]) : col.name;
            const tipId = `entity-col-desc-${col.id}`;
            const mandatoryIndicator = col.is_mandatory ? " *" : "";

            return (
              <HeaderWithIcon>
                <span>
                  {label}
                  {mandatoryIndicator}
                </span>

                <ReactTooltip
                  id={tipId}
                  effect="solid"
                  delayHide={200}
                  multiline={true}
                />

                {col.is_not_applicable && (
                  <NonVisibileContainer
                    data-tip={t("This attribute is marked as not applicable")}
                    data-for="not-applicable-tip"
                  >
                    <NotApplicableIcon
                      width={15.7}
                      height={15.7}
                      fill={theme.errorColor}
                    />
                  </NonVisibileContainer>
                )}
              </HeaderWithIcon>
            );
          },

          accessorKey: col.id,
          meta:
            !col.is_mandatory && isEditable && col.is_editable !== false
              ? { contextMenu }
              : undefined,

          cell: ({
            getValue,
          }: {
            row: { original: IRow };
            getValue: Getter<string | null | boolean | { name: string }[]>;
          }) => {
            const rawValue = getValue() as CollectionAttributeDisplayValue;
            const query = searchQuery.trim().toLowerCase();
            const highlighted =
              !!query &&
              valueToDisplayStrings(col, rawValue).some((s) =>
                s.toLowerCase().includes(query)
              );
            const displayContent = getCollectionAttributeDisplayValue(
              col,
              rawValue,
              {
                theme,
                t,
                user,
                roleIsSellerAdmin,
                hasPermission,
                storePath,
                adminPath,
                onDocumentMoreClick: (docs, attributeName) =>
                  setDocumentMoreDocs({ docs, attributeName }),
                onObjectReferenceMoreClick: (
                  references,
                  attributeName,
                  targetObjectType
                ) =>
                  setObjectReferenceMore({
                    references,
                    attributeName,
                    targetObjectType,
                  }),
                onProductReferenceMoreClick: (references, attributeName) =>
                  setProductReferenceMore({ references, attributeName }),
              }
            );
            // For plain string/number values, bold the matching portion inline.
            // For complex React elements (links, badges, etc.) the yellow
            // background via $active is the visual indicator.
            const isPlainText =
              typeof displayContent === "string" ||
              typeof displayContent === "number";
            return (
              <SearchHighlight $active={highlighted}>
                {highlighted && isPlainText ? (
                  <span>
                    {getHighlightSegments(
                      String(displayContent),
                      searchQuery
                    ).map((segment, index) =>
                      segment.matched ? (
                        <b key={index}>{segment.text}</b>
                      ) : (
                        segment.text
                      )
                    )}
                  </span>
                ) : (
                  displayContent
                )}
              </SearchHighlight>
            );
          },
        };
      });

    if (isEditable && hasEditableColumns) {
      cols.push({
        header: () => <></>,
        accessorKey: "uuid" as string,
        cell: ({
          getValue,
          row,
        }: {
          row: { original: IRow };
          getValue: Getter<string>;
        }) => (
          <ActionsCell id="table-action-buttons">
            <GhostButtonMediumV2
              onClick={() => handleEditRow(row.original)}
              title={t("Edit")}
            >
              <EditIcon width={16} height={16} />
            </GhostButtonMediumV2>
            <GhostButtonMediumV2
              onClick={() =>
                setDeleteConfirm({
                  show: true,
                  rowId: getValue() as string,
                })
              }
              title={t("Delete")}
            >
              <DeleteIcon width={16} height={16} />
            </GhostButtonMediumV2>
          </ActionsCell>
        ),
      } as typeof cols[number]);
    }

    if (
      enable_change_requests &&
      entity.status !== "archived" &&
      !hasPermission(ENTITY_MODIFY_PERMISSIONS[entityType])
    ) {
      cols.push({
        header: () => <></>,
        accessorKey: "uuid" as string,
        cell: ({
          getValue,
          row,
        }: {
          row: { original: IRow };
          getValue: Getter<string>;
        }) => (
          <ActionsCell>
            <GhostButtonMediumV2
              onClick={() => handleChangeRequestForm(row.original)}
              title={t("Update")}
            >
              <QuoteIcon width={16} height={16} />
            </GhostButtonMediumV2>
          </ActionsCell>
        ),
      } as typeof cols[number]);
    }

    return cols;
  }, [
    visibleColumns,
    enable_change_requests,
    theme,
    isSubmittingApplicability,
    t,
    user,
    roleIsSellerAdmin,
    storePath,
    adminPath,
    entityType,
    isEditable,
    hasEditableColumns,
    hasPermission,
    entity.status,
    searchQuery,
  ]);

  const changeRequestContext = useMemo(
    () => ({
      variant: "collection" as const,
      collectionData: {
        collection,
        entityName: entity.name,
        rowId: changeRequestFormRow?.uuid,
      },
    }),
    [collection, entity.name, changeRequestFormRow?.uuid]
  );

  const onSubmitChangeRequest = async (formValues: ChangeRequestFormValues) => {
    setIsSubmitting(true);
    try {
      await submitChangeRequest({
        tenant_id,
        entity,
        context: changeRequestContext,
        formValues,
        onSuccess: () => setShowForm(false),
      });
      notifySuccess(t("Change request submitted successfully."));
    } catch (error) {
      console.error(error);
      notifyError(t("Failed to submit change request."));
    } finally {
      setIsSubmitting(false);
    }
  };

  const title = toTitleCase(
    collection.display_name ? t([collection.display_name]) : t(collection.name)
  );

  if (visibleColumns.length === 0) {
    return <></>;
  }

  const handleConfirmApplicability = async () => {
    if (pendingAction) {
      setShowConfirmDialog(false);
      try {
        setIsSubmittingApplicability(true);
        await axios.patch(
          `/v2/tenants/${tenantId}/${entityType + "s"}/${
            entity.id
          }/collections/${collection.id}/columns/${pendingAction.columnId}`,
          { is_not_applicable: pendingAction.newStatus }
        );
        await mutateEntity();
        notifySuccess(
          t(
            pendingAction.newStatus
              ? "Column marked as not applicable"
              : "Column marked as applicable"
          )
        );
      } catch (error) {
        notifyError(
          t(
            pendingAction.newStatus
              ? "Failed to mark column as not applicable"
              : "Failed to mark column as applicable"
          ),
          { error }
        );
      } finally {
        setIsSubmittingApplicability(false);
        setPendingAction(null);
      }
    }
  };
  const filterableColumnDefs = visibleColumns.filter(
    (col) =>
      col.input_type !== "multiline_entry" &&
      !isDocumentAttribute(col.input_type) &&
      col.is_filter_eligible &&
      ((activeFilters[col.id]?.length ?? 0) > 0 ||
        (columnFilterOptions[col.id]?.length ?? 0) > 0 ||
        // AP-1016 (Advanced Filters, Part 1): numeric columns are always
        // filterable via operator + value, independent of which discrete
        // display strings happen to exist in the data.
        col.input_type === "numeric")
  );

  const hasActiveFilters = Object.values(activeFilters).some(
    (chips) => chips.length > 0
  );

  const filtersSlot =
    filterableColumnDefs.length > 0 ? (
      <>
        {filterableColumnDefs.map((col) => {
          const label = col.display_name ? t([col.display_name]) : col.name;
          if (col.input_type === "numeric") {
            return (
              <NumericFilterButton
                key={col.id}
                filterType={col.id}
                label={label}
                operators={NUMERIC_FILTER_OPERATORS}
                preselectedFilters={activeFilters[col.id] ?? []}
                setter={handleFilterChange}
                usePortal
              />
            );
          }
          return (
            <FilterBy
              key={col.id}
              filter={col.id}
              label={label}
              header={label}
              options={columnFilterOptions[col.id] ?? []}
              preselectedFilters={activeFilters[col.id] ?? []}
              setter={handleFilterChange}
              size="small"
              usePortal
            />
          );
        })}
      </>
    ) : undefined;

  if (
    !visibleColumns.length ||
    !collectionHasDisplayableValues(
      collection,
      object_reference_enabled,
      isEditable
    )
  ) {
    return null;
  }

  return (
    <div className={PROFILE_DETAIL_CLASSES.collectionCard}>
      <AccordionCard
        title={title}
        expanded={true}
        actionChildren={() => <></>}
        hideHeaderBorder={true}
      >
        <CollectionTableContainer>
          <TablePageLayout
            columns={tableColumns}
            data={filteredTableData}
            isLoading={false}
            error={undefined}
            enableDefaultSort={true}
            lastChildleftAlign
            Placeholder={<TablePlaceholder />}
            highlightLinks
            searchQuery={searchQuery}
            searchPlaceholder={t("Search...")}
            onSearchChange={
              collection.enable_collection_search && tableData.length > 0
                ? (e) => setSearchQuery(e.target.value)
                : undefined
            }
            onSearchClear={() => setSearchQuery("")}
            totalCount={
              filteredTableData.length > 0 && tableData.length > 0
                ? tableData.length
                : undefined
            }
            searchFilterCount={
              filteredTableData.length > 0
                ? filteredTableData.length
                : undefined
            }
            totalCountLabel={
              filteredTableData.length > 0
                ? tableData.length === 1
                  ? t("item")
                  : t("items")
                : undefined
            }
            filtersSlot={
              collection.enable_collection_filter && tableData.length > 0
                ? filtersSlot
                : undefined
            }
            showFilters={
              collection.enable_collection_filter && tableData.length > 0
                ? !!filtersSlot
                : false
            }
            showClearAll={hasActiveFilters}
            onClearAll={handleClearAllFilters}
            clearAllLabel={t("Clear Filters")}
            showMeta={true}
            containerStyle={{ marginTop: 0 }}
            maxHeight="unset"
          />
        </CollectionTableContainer>
        {isEditable && (
          <div>
            <TertiaryButtonMediumV2
              style={{ width: "fit-content" }}
              onClick={handleAddRow}
            >
              <PlusIcon />
              {t("Add")}
            </TertiaryButtonMediumV2>
          </div>
        )}
        {showSlideOut && (
          <EntityCollectionSlideOut
            show={showSlideOut}
            onClose={handleCloseSlideOut}
            onSubmit={handleSubmitRow}
            columns={visibleColumns}
            row={editingRow}
            rowUuid={editingRowUuid}
            isNewRow={isNewRow}
            isSubmitting={isSubmitting}
            collection={collection}
            excludeProfileId={entity_id ?? entity.id}
            tenantId={tenantId}
          />
        )}
        {deleteConfirm.show && deleteConfirm.rowId && (
          <ConfirmDialog
            show={deleteConfirm.show}
            closeDialog={() => setDeleteConfirm({ show: false, rowId: null })}
            confirmMessage={t(
              "Are you sure you want to remove this item? This action cannot be undone."
            )}
            title={t("Remove Item")}
            handleConfirm={() => handleDeleteRow(deleteConfirm.rowId!)}
            confirmBtnType="destructive"
            confirmBtnLabel={t("Remove")}
            useV2Buttons={true}
          />
        )}
        {pendingObjectRefRowUpdate && (
          <ObjectReferenceChangeConfirmDialog
            show={true}
            onClose={() => setPendingObjectRefRowUpdate(null)}
            onConfirm={handleConfirmObjectRefRowUpdate}
          />
        )}
        <ChangeRequestFormModal
          open={showForm}
          onClose={() => {
            setShowForm(false);
            setChangeRequestFormRow(null);
          }}
          variant={changeRequestContext.variant}
          collectionData={changeRequestContext.collectionData}
          onSubmit={onSubmitChangeRequest}
          submitting={isSubmitting}
          assigneePermissions={[ENTITY_MODIFY_PERMISSIONS[entityType]]}
        />
        <ConfirmDialog
          show={showConfirmDialog}
          closeDialog={() => {
            setShowConfirmDialog(false);
            setPendingAction(null);
          }}
          confirmMessage={
            pendingAction?.newStatus
              ? t(
                  "Are you sure you want to mark this column as not applicable?"
                )
              : t("Are you sure you want to mark this column as applicable?")
          }
          handleConfirm={handleConfirmApplicability}
        />
        {documentMoreDocs && (
          <DocumentMoreModal
            docs={documentMoreDocs.docs}
            attributeName={documentMoreDocs.attributeName}
            onClose={() => setDocumentMoreDocs(null)}
          />
        )}
        {objectReferenceMore && (
          <ObjectReferenceMoreModal
            references={objectReferenceMore.references}
            attributeName={objectReferenceMore.attributeName}
            targetObjectType={objectReferenceMore.targetObjectType}
            onClose={() => setObjectReferenceMore(null)}
            linkOptions={{ isPortfolio: false, storePath, adminPath }}
            accessOptions={{
              isPortfolio: false,
              user,
              roleIsSellerAdmin,
              hasPermission,
            }}
          />
        )}
        {productReferenceMore && (
          <ProductCardsMoreModal
            references={productReferenceMore.references}
            attributeName={productReferenceMore.attributeName}
            onClose={() => setProductReferenceMore(null)}
            storePath={storePath}
            adminPath={adminPath}
            accessOptions={{
              isPortfolio: false,
              user,
              roleIsSellerAdmin,
              hasPermission,
            }}
          />
        )}
      </AccordionCard>
      {showDuplicateRowError && (
        <DuplicateItemModal
          show={showDuplicateRowError}
          onClose={() => setShowDuplicateRowError(false)}
        />
      )}
    </div>
  );
}
